import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import RatingReact from 'react-rating';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { customer_review, get_reviews, messageClear, product_details } from '../store/reducers/homeReducer';
import { DISPLAY } from './home/ui';

// Reseñas del producto (diseño producto.html): resumen con barras por
// estrella a la izquierda, lista paginada a la derecha.

const PER_PAGE = 5; // lo que devuelve GET /store/reviews/{id} por página
const AV_COLORS = ['#0891b2', '#f87171', '#1e3a8a', '#16a34a'];

const starsText = (n) => {
    const full = Math.max(0, Math.min(5, Math.round(Number(n) || 0)));
    return '★'.repeat(full) + '☆'.repeat(5 - full);
};

const initials = (name = '') =>
    name.trim().split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('') || '🐾';

const fecha = (iso) => {
    const d = new Date(iso);
    return isNaN(d) ? '' : d.toLocaleDateString('es-GT', { day: 'numeric', month: 'short', year: 'numeric' });
};

const Reviews = ({ product }) => {
    const dispatch = useDispatch();
    const [pageNumber, setPageNumber] = useState(1);
    const [writing, setWriting] = useState(false);
    const [rat, setRat] = useState(0);
    const [re, setRe] = useState('');

    const { userInfo } = useSelector(state => state.auth);
    const { successMessage, reviews, rating_review, totalReview } = useSelector(state => state.home);

    // Las reseñas son del producto, no de la variante: `_id` es la variante.
    const productId = product.productId ?? product._id;

    const review_submit = (e) => {
        e.preventDefault();
        if (!rat) { toast.error('Elige una calificación de 1 a 5 estrellas'); return; }
        dispatch(customer_review({ name: userInfo.name, review: re, rating: rat, productId }));
    };

    useEffect(() => {
        if (successMessage) {
            toast.success(successMessage);
            dispatch(get_reviews({ productId, pageNumber }));
            dispatch(product_details(product.slug));
            setRat(0);
            setRe('');
            setWriting(false);
            dispatch(messageClear());
        }
    }, [successMessage]);

    useEffect(() => {
        if (productId) dispatch(get_reviews({ productId, pageNumber }));
    }, [pageNumber, productId]);

    const countOf = (star) => rating_review.find(r => Number(r.rating) === star)?.sum || 0;
    const avg = Number(product.rating) || 0;

    return (
        <div className='grid grid-cols-[300px_minmax(0,1fr)] md-lg:grid-cols-1 gap-12'>
            <div className='bg-[#faf9f6] border border-[#f1efea] rounded-[20px] p-7 self-start'>
                <div className='font-extrabold text-[#1e3a8a] text-[56px] leading-none' style={DISPLAY}>
                    {avg.toFixed(1)}<small className='text-[20px] text-[#64748b] font-semibold'>/5</small>
                </div>
                <div className='text-[#fbbf24] text-[18px] tracking-[2px] mt-[6px] mb-[2px]'>{starsText(avg)}</div>
                <div className='text-[13px] text-[#64748b] mb-5'>
                    {totalReview ? `Basado en ${totalReview} reseña${totalReview === 1 ? '' : 's'}` : 'Aún no hay reseñas'}
                </div>
                {[5, 4, 3, 2, 1].map(star => {
                    const n = countOf(star);
                    const pct = totalReview ? (100 * n) / totalReview : 0;
                    return (
                        <div key={star} className='grid grid-cols-[28px_minmax(0,1fr)_28px] gap-[10px] items-center text-[12.5px] text-[#64748b] mb-2'>
                            <span>{star}★</span>
                            <div className='h-[7px] rounded-[7px] bg-[#e8e6e1] overflow-hidden'>
                                <i className='block h-full bg-[#fbbf24] rounded-[7px]' style={{ width: `${pct}%` }} />
                            </div>
                            <span>{n}</span>
                        </div>
                    );
                })}
                {userInfo ? (
                    <button type='button' onClick={() => setWriting(w => !w)}
                        className='mt-[18px] w-full h-[46px] rounded-[12px] border-[1.5px] border-[#1e3a8a] text-[#1e3a8a] font-bold text-[14px] hover:bg-[#1e3a8a] hover:text-white transition-all'>
                        {writing ? 'Cancelar' : 'Escribir una reseña'}
                    </button>
                ) : (
                    <Link to='/login'
                        className='mt-[18px] w-full h-[46px] rounded-[12px] border-[1.5px] border-[#1e3a8a] text-[#1e3a8a] font-bold text-[14px] hover:bg-[#1e3a8a] hover:text-white transition-all grid place-items-center'>
                        Inicia sesión para opinar
                    </Link>
                )}
            </div>

            <div className='flex flex-col'>
                {writing && userInfo && (
                    <form onSubmit={review_submit} className='mb-6 p-5 rounded-[18px] border-[1.5px] border-[#cffafe] bg-[#ecfeff]/50 flex flex-col gap-3'>
                        <div className='flex items-center gap-3'>
                            <span className='text-[13px] font-bold text-[#0f172a]'>Tu calificación</span>
                            <RatingReact
                                onChange={setRat}
                                initialRating={rat}
                                emptySymbol={<span className='text-[#e8e6e1] text-[26px] px-[1px]'>★</span>}
                                fullSymbol={<span className='text-[#fbbf24] text-[26px] px-[1px]'>★</span>}
                            />
                        </div>
                        <textarea value={re} onChange={e => setRe(e.target.value)} required rows={4}
                            placeholder='¿Qué le pareció a tu peludo?'
                            className='w-full rounded-[12px] border-[1.5px] border-[#e8e6e1] bg-white p-3 text-[14.5px] outline-none focus:border-[#0891b2]' />
                        <div className='flex items-center justify-between gap-3 flex-wrap'>
                            <span className='text-[12.5px] text-[#64748b]'>Tu reseña se publica cuando la aprobemos.</span>
                            <button className='h-[44px] px-6 rounded-[12px] bg-[#0891b2] hover:bg-[#0e7490] text-white font-bold text-[14px] transition-colors'>Publicar reseña</button>
                        </div>
                    </form>
                )}

                {reviews.length === 0 ? (
                    <p className='text-[14.5px] text-[#64748b] m-0'>Sé el primero en contar qué tal le fue a tu compañero con este producto.</p>
                ) : reviews.map((r, i) => (
                    <div key={r._id || i} className='py-[22px] first:pt-0 border-b border-[#f1efea]'>
                        <div className='flex items-center gap-3 mb-[10px]'>
                            <div className='w-10 h-10 rounded-full grid place-items-center font-extrabold text-[14px] text-white flex-shrink-0'
                                style={{ background: AV_COLORS[i % AV_COLORS.length] }}>
                                {initials(r.name)}
                            </div>
                            <div className='font-bold text-[#0f172a] text-[14.5px]'>{r.name}</div>
                            <span className='ml-auto text-[12.5px] text-[#64748b]'>{fecha(r.date)}</span>
                        </div>
                        <div className='text-[#fbbf24] tracking-[1px] text-[14px]'>{starsText(r.rating)}</div>
                        <p className='text-[14.5px] leading-[1.6] text-[#334155] mt-[6px] mb-0'>{r.review}</p>
                    </div>
                ))}

                {totalReview > PER_PAGE && (
                    <div className='flex justify-end gap-2 pt-5' aria-label='Páginas de reseñas'>
                        {Array.from({ length: Math.ceil(totalReview / PER_PAGE) }, (_, i) => i + 1).map(n => (
                            <button key={n} type='button' onClick={() => setPageNumber(n)} aria-current={n === pageNumber ? 'page' : undefined}
                                className={`w-9 h-9 rounded-[10px] text-[13px] font-bold border-[1.5px] transition-all ${n === pageNumber ? 'bg-[#1e3a8a] border-[#1e3a8a] text-white' : 'bg-white border-[#e8e6e1] text-[#1e3a8a] hover:border-[#0891b2]'}`}>
                                {n}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Reviews;
